import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import weightLossMessageImage from './blogimg/weightloss/weightlossmessage.jpg';
import balancedDietImage from './blogimg/weightloss/balanceddiet.jpg';
import exerciseTipsImage from './blogimg/weightloss/exercisetips.jpg';
import morningRoutineImage from './blogimg/weightloss/morningroutine.jpg';
import supportCommunityImage from './blogimg/weightloss/supportcommunity.jpg';
import alliWeightLossImage from './blogimg/weightloss/alliweightloss.jpg';
import monitorYourProgressImage from './blogimg/weightloss/monitoryourprogress.jpg';
import stayHydratedImage from './blogimg/weightloss/stayhydrated.jpg';
import getAdequateSleepImage from './blogimg/weightloss/getadequatesleep.jpg';

/**
 * Helper function to map the filename from Firestore to a local image import.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'weightlossmessage.jpg':
      return weightLossMessageImage;
    case 'balanceddiet.jpg':
      return balancedDietImage;
    case 'exercisetips.jpg':
      return exerciseTipsImage;
    case 'morningroutine.jpg':
      return morningRoutineImage;
    case 'supportcommunity.jpg':
      return supportCommunityImage;
    case 'alliweightloss.jpg':
      return alliWeightLossImage;
    case 'monitoryourprogress.jpg':
      return monitorYourProgressImage;
    case 'stayhydrated.jpg':
      return stayHydratedImage;
    case 'getadequatesleep.jpg':
      return getAdequateSleepImage;
    default:
      return null;
  }
}

const BlogWeightlossPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Weight Loss Tips: Effective Strategies to Shed Pounds & Stay Healthy";

    // Helper function to add or update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Discover practical weight loss tips for a healthier lifestyle. Learn about balanced diets, exercise routines, supplements, and proven strategies to achieve safe and sustainable results."
    );
    setMetaTag(
      "keywords",
      "weight loss tips, balanced diet, exercise, supplements, healthy lifestyle, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Proven Weight Loss Tips: How to Lose Weight Safely & Sustainably",
      true
    );
    setMetaTag(
      "og:description",
      "Explore effective strategies, from balanced diets and exercise to recommended supplements for achieving lasting weight loss.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/weightlossmessage.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/weight-loss-tips",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
      // Remove or reset meta tags here if desired
    };
  }, []);

  // 2️⃣ Fetch Article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Firestore path: /products/blog/weightloss
        const colRef = collection(db, 'products', 'blog', 'weightloss');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No blog document found for weightloss');
        }
      } catch (error) {
        // console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Render Block Function
  const renderBlock = (block, index) => {
    const { tag, text } = block;
    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1">
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2">
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3">{text}</h3>
          </React.Fragment>
        );
      case 'h4':
        return <h4 key={index}>{text}</h4>;
      case 'p':
        return (
          <p key={index} className="recipe-p">
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul">
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol">
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === 'object') {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return <div key={index}>{text}</div>;
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 4️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 from Firestore data (if any) */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}

        {/* Render each section from Firestore */}
        {article.sections &&
          article.sections.map((section, sectionIndex) => (
            <section key={sectionIndex}>
              {section.blocks &&
                section.blocks.map((block, blockIndex) => renderBlock(block, blockIndex))
              }
            </section>
          ))}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() => (window.location.href = 'https://purehealthnexus.com/weightloss')}
        >
          See Weigh Loss Here
        </button>
      </div>
    </div>
  );
};

export default BlogWeightlossPage;
